/**
 * Source de trafic (`?s=…`) — attribution de l'origine des joueurs.
 *
 * Au chargement, on lit le paramètre `s`, on le **sanitise** (uniquement
 * `[A-Za-z0-9_-]`, 64 car. max) puis on le **retire de l'URL**
 * (`history.replaceState`) pour éviter tout partage / copier-coller de la
 * valeur. La source est ensuite attachée à l'événement `game_started`
 * (cf. store/useGame.ts), donc liée à la session de jeu.
 *
 * On tolère les liens **malformés** (`/&s=valeur` sans `?`) : dans ce cas le
 * `s=` se retrouve collé au chemin, donc on scanne l'URL entière plutôt que
 * la seule query string. Le fragment est retiré quel que soit son séparateur.
 *
 * Ce module s'exécute une seule fois à l'import (effet de bord). Il est
 * importé tôt dans `main.tsx` pour garantir le nettoyage de l'URL quelle
 * que soit la route.
 */

/**
 * Sanitise une valeur de source : ne garde que `[A-Za-z0-9_-]`, 64 car. max.
 * Renvoie `null` si rien d'exploitable (fonction pure, testable).
 */
export function sanitizeSource(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const clean = raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
  return clean.length > 0 ? clean : null;
}

/**
 * Extrait `s=` d'un href (query `?s=` OU lien malformé `&s=`) et renvoie
 * l'URL nettoyée de ce fragment. Fonction pure, testable.
 */
export function parseAndStripSource(href: string): {
  source: string | null;
  url: string;
} {
  const re = /[?&]s=([^&#]*)/;
  const m = href.match(re);
  if (!m) return { source: null, url: href };
  const source = sanitizeSource(m[1]);
  // Retire le fragment ; garde le `?` si c'était le 1er paramètre.
  let url = href.replace(re, (full) => (full[0] === "?" ? "?" : ""));
  // Recolle proprement les séparateurs orphelins (`?&`, `?` ou `&` en fin
  // ou juste avant un `#`).
  url = url
    .replace(/\?&/, "?")
    .replace(/\?(#|$)/, "$1")
    .replace(/&(#|$)/, "$1");
  return { source, url };
}

function captureAndStrip(): string | null {
  const { source, url } = parseAndStripSource(window.location.href);
  if (url !== window.location.href) {
    history.replaceState(null, "", url);
  }
  return source;
}

/** Source de trafic sanitisée pour cette session, ou `null`. */
export const trafficSource = captureAndStrip();
